import React from "react";

import { Button, Card, Flex, Icon, SkeletonWrapper, Tooltip, Typography } from "components";

export type ListCardDataPair = {
  label: string;
  value: React.ReactNode;
  children?: React.ReactNode;
  /** Explains the row; shown in a tooltip next to the label */
  tooltip?: string;
  /** The value is a fallback rather than something configured; rendered muted */
  isDefault?: boolean;
};

export type ListCardProps = {
  title?: string;
  titleButton?: {
    label: string;
    onClick: () => void;
  };
  dataPairs?: ListCardDataPair[];
  className?: string;
  dataTestId?: string;
  isOutlined?: boolean;
  isSublist?: boolean;
  isLoading?: boolean;
};

export const ListCard: React.FC<ListCardProps> = ({ title, titleButton, dataPairs, className = "", dataTestId = "", isOutlined, isSublist, isLoading }) => (
  <Card dataTestId={dataTestId} className={`list-card ${isOutlined ? "outlined" : ""} padding-150 ${className}`}>
    <Flex className={"padding-bottom-100"} align="center" justify="space-between">
      <SkeletonWrapper className="margin-0 skeleton-rectangular" width="50%" height="2rem" type="block" isLoading={isLoading}>
        {isSublist ? <Typography variant="h5">{title}</Typography> : <Typography variant="h3">{title}</Typography>}
        {titleButton && (
          <Button type="tertiary" className="title-button margin-0" onClick={titleButton.onClick}>
            {titleButton.label}
          </Button>
        )}
      </SkeletonWrapper>
    </Flex>
    {dataPairs?.map((record, index) => (
      <div key={index}>
        <Flex gap={64} justify="space-between">
          <SkeletonWrapper className="margin-0 skeleton-rectangular" width="100%" height="1.5rem" type="block" isLoading={isLoading}>
            <Flex className="list-card-label" gap={8} align="center">
              <Typography variant="paragraph-m">{record.label}</Typography>
              {record.tooltip && (
                <Tooltip variant="neutral" title={record.tooltip}>
                  <Icon iconName="tabler/info-circle" size="s" />
                </Tooltip>
              )}
            </Flex>
          </SkeletonWrapper>
          <SkeletonWrapper className="margin-0 skeleton-rectangular" width="100%" height="1.5rem" type="block" isLoading={isLoading}>
            <Tooltip variant="neutral" title={record.isDefault ? "Platform default (not configured)" : undefined}>
              <Typography variant="paragraph-m" bold={!record.isDefault} color={record.isDefault ? "grey-600" : undefined} className="word-wrap">
                {record.value}
              </Typography>
            </Tooltip>
          </SkeletonWrapper>
        </Flex>
        {(index + 1 !== dataPairs.length || record.children) && <hr />}
        {record.children}
      </div>
    ))}
  </Card>
);
